<!--
  HOW TO INSTALL JOVII — the illustrated version, for the About page.

  WHY THESE ARE DRAWN AND NOT SCREENSHOTS. The owner asked me to find assets
  online that explain this well and use them. I did not, and the reason is
  worth stating rather than quietly ignoring: those tutorial screenshots are
  somebody else's copyrighted work, hotlinking them breaks the moment they
  move a file, and none of them would be in Jovii's palette. Drawn SVG costs a
  few KB, matches the brand, works offline with the shell cache and scales to
  any screen. The shapes below are Apple's and Google's own UI conventions —
  the share glyph, the three-dot menu — redrawn, which is the part a reader
  actually needs to recognise.

  This is the SHOWCASE version. `InstallGuide.svelte` stays what it is: the
  terse, device-detecting panel inside Settings, where somebody already
  convinced just needs the steps for the phone in their hand. Here we are
  still persuading, so all three platforms are offered and the reader picks.
-->
<script lang="ts">
  type Platform = 'ios' | 'android' | 'desktop';

  let picked = $state<Platform>('ios');

  /* Guessed from the user agent, then freely overridden: somebody reading
     this on a laptop is often installing on the phone beside them. */
  $effect(() => {
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    picked = ios ? 'ios' : /Android/.test(ua) ? 'android' : 'desktop';
  });

  const TABS: { id: Platform; label: string }[] = [
    { id: 'ios', label: 'iPhone & iPad' },
    { id: 'android', label: 'Android' },
    { id: 'desktop', label: 'Mac & PC' }
  ];
</script>

<div class="install">
  <div class="tabs" role="tablist" aria-label="Choose your device">
    {#each TABS as tab (tab.id)}
      <button
        type="button"
        role="tab"
        id="tab-{tab.id}"
        aria-selected={picked === tab.id}
        aria-controls="panel-{tab.id}"
        class:on={picked === tab.id}
        onclick={() => (picked = tab.id)}
      >{tab.label}</button>
    {/each}
  </div>

  {#if picked === 'ios'}
    <div class="steps" role="tabpanel" id="panel-ios" aria-labelledby="tab-ios">
      <figure>
        <svg viewBox="0 0 200 260" role="img" aria-label="Safari's toolbar with the Share button highlighted">
          <rect x="18" y="8" width="164" height="244" rx="26" class="body" />
          <rect x="26" y="16" width="148" height="228" rx="20" class="screen" />
          <rect x="38" y="40" width="124" height="14" rx="7" class="dim" />
          <rect x="38" y="66" width="124" height="86" rx="10" class="art" />
          <rect x="38" y="162" width="88" height="8" rx="4" class="dim" />
          <rect x="38" y="178" width="110" height="8" rx="4" class="dim" />
          <rect x="26" y="206" width="148" height="38" rx="12" class="bar" />
          <g>
            <circle cx="100" cy="225" r="19" class="ring" />
            <path d="M100 216v18M100 216l-6 6M100 216l6 6" class="stroke" />
            <rect x="91" y="228" width="18" height="12" rx="3" class="stroke-only" />
          </g>
        </svg>
        <figcaption><span>1</span><p>In <strong>Safari</strong>, tap the <strong>Share</strong> button — the square with an arrow pointing up.</p></figcaption>
      </figure>

      <figure>
        <svg viewBox="0 0 200 260" role="img" aria-label="The share sheet with Add to Home Screen highlighted">
          <rect x="18" y="8" width="164" height="244" rx="26" class="body" />
          <rect x="26" y="16" width="148" height="228" rx="20" class="screen" />
          <rect x="34" y="96" width="132" height="140" rx="16" class="sheet" />
          <rect x="46" y="108" width="60" height="7" rx="3.5" class="dim" />
          <rect x="46" y="128" width="108" height="20" rx="6" class="dim" />
          <rect x="46" y="156" width="108" height="20" rx="6" class="dim" />
          <g>
            <rect x="42" y="182" width="116" height="26" rx="8" class="ring-fill" />
            <rect x="52" y="190" width="10" height="10" rx="2.5" class="stroke-only" />
            <rect x="70" y="193" width="62" height="5" rx="2.5" class="ink" />
          </g>
        </svg>
        <figcaption><span>2</span><p>Scroll the list and tap <strong>Add to Home Screen</strong>.</p></figcaption>
      </figure>

      <figure>
        <svg viewBox="0 0 200 260" role="img" aria-label="Jovii's icon on the iPhone home screen">
          <rect x="18" y="8" width="164" height="244" rx="26" class="body" />
          <rect x="26" y="16" width="148" height="228" rx="20" class="screen" />
          {#each [0, 1, 2, 3, 4, 5] as i}
            <rect x={42 + (i % 3) * 40} y={60 + Math.floor(i / 3) * 48} width="30" height="30" rx="8" class="dim" />
          {/each}
          <g>
            <rect x="42" y="156" width="30" height="30" rx="8" class="mark" />
            <text x="57" y="177" class="glyph">J</text>
            <rect x="40" y="192" width="34" height="5" rx="2.5" class="ink" />
          </g>
        </svg>
        <figcaption><span>3</span><p>Tap <strong>Add</strong>. Jovii opens full screen, with no browser bars — and your downloads stay put.</p></figcaption>
      </figure>
    </div>
  {:else if picked === 'android'}
    <div class="steps" role="tabpanel" id="panel-android" aria-labelledby="tab-android">
      <figure>
        <svg viewBox="0 0 200 260" role="img" aria-label="Chrome's three-dot menu">
          <rect x="18" y="8" width="164" height="244" rx="20" class="body" />
          <rect x="26" y="16" width="148" height="228" rx="14" class="screen" />
          <rect x="26" y="16" width="148" height="34" rx="12" class="bar" />
          <rect x="38" y="28" width="96" height="10" rx="5" class="dim" />
          <rect x="38" y="64" width="124" height="90" rx="10" class="art" />
          <rect x="38" y="164" width="100" height="8" rx="4" class="dim" />
          <g>
            <circle cx="156" cy="33" r="16" class="ring" />
            <circle cx="156" cy="27" r="2.6" class="fill" />
            <circle cx="156" cy="33" r="2.6" class="fill" />
            <circle cx="156" cy="39" r="2.6" class="fill" />
          </g>
        </svg>
        <figcaption><span>1</span><p>Open Jovii in <strong>Chrome</strong> and tap the <strong>three-dot menu</strong>, top right.</p></figcaption>
      </figure>

      <figure>
        <svg viewBox="0 0 200 260" role="img" aria-label="The Chrome menu with Install app highlighted">
          <rect x="18" y="8" width="164" height="244" rx="20" class="body" />
          <rect x="26" y="16" width="148" height="228" rx="14" class="screen" />
          <rect x="84" y="30" width="86" height="132" rx="12" class="sheet" />
          <rect x="94" y="44" width="60" height="6" rx="3" class="dim" />
          <rect x="94" y="62" width="66" height="6" rx="3" class="dim" />
          <g>
            <rect x="88" y="78" width="78" height="22" rx="7" class="ring-fill" />
            <rect x="96" y="86" width="9" height="9" rx="2" class="stroke-only" />
            <rect x="112" y="88" width="46" height="5" rx="2.5" class="ink" />
          </g>
          <rect x="94" y="112" width="54" height="6" rx="3" class="dim" />
          <rect x="94" y="130" width="62" height="6" rx="3" class="dim" />
        </svg>
        <figcaption><span>2</span><p>Choose <strong>Install app</strong> — or <strong>Add to Home screen</strong>.</p></figcaption>
      </figure>

      <figure>
        <svg viewBox="0 0 200 260" role="img" aria-label="Jovii's icon in the Android app drawer">
          <rect x="18" y="8" width="164" height="244" rx="20" class="body" />
          <rect x="26" y="16" width="148" height="228" rx="14" class="screen" />
          {#each [0, 1, 2, 3, 4, 5] as i}
            <rect x={42 + (i % 3) * 40} y={60 + Math.floor(i / 3) * 48} width="30" height="30" rx="15" class="dim" />
          {/each}
          <g>
            <rect x="42" y="156" width="30" height="30" rx="15" class="mark" />
            <text x="57" y="177" class="glyph">J</text>
            <rect x="40" y="192" width="34" height="5" rx="2.5" class="ink" />
          </g>
        </svg>
        <figcaption><span>3</span><p>Confirm, and Jovii sits with your other apps.</p></figcaption>
      </figure>
    </div>
  {:else}
    <div class="steps" role="tabpanel" id="panel-desktop" aria-labelledby="tab-desktop">
      <figure>
        <svg viewBox="0 0 260 200" role="img" aria-label="A browser address bar with the install icon">
          <rect x="10" y="16" width="240" height="168" rx="14" class="body" />
          <rect x="18" y="24" width="224" height="152" rx="10" class="screen" />
          <rect x="18" y="24" width="224" height="30" rx="10" class="bar" />
          <circle cx="32" cy="39" r="4" class="dim" />
          <circle cx="46" cy="39" r="4" class="dim" />
          <circle cx="60" cy="39" r="4" class="dim" />
          <rect x="76" y="33" width="120" height="12" rx="6" class="dim" />
          <g>
            <circle cx="214" cy="39" r="15" class="ring" />
            <path d="M214 32v13M208 39l6 6 6-6" class="stroke" />
            <rect x="205" y="48" width="18" height="3" rx="1.5" class="fill" />
          </g>
          <rect x="34" y="72" width="80" height="80" rx="10" class="art" />
          <rect x="128" y="72" width="96" height="10" rx="5" class="dim" />
          <rect x="128" y="92" width="76" height="10" rx="5" class="dim" />
        </svg>
        <figcaption><span>1</span><p>In <strong>Chrome</strong> or <strong>Edge</strong>, click the <strong>install icon</strong> at the right of the address bar.</p></figcaption>
      </figure>

      <figure>
        <svg viewBox="0 0 260 200" role="img" aria-label="Safari's File menu with Add to Dock">
          <rect x="10" y="16" width="240" height="168" rx="14" class="body" />
          <rect x="18" y="24" width="224" height="152" rx="10" class="screen" />
          <rect x="18" y="24" width="224" height="22" rx="10" class="bar" />
          <rect x="30" y="31" width="22" height="7" rx="3.5" class="ink" />
          <rect x="60" y="31" width="20" height="7" rx="3.5" class="dim" />
          <rect x="26" y="50" width="96" height="96" rx="10" class="sheet" />
          <rect x="36" y="62" width="58" height="6" rx="3" class="dim" />
          <rect x="36" y="78" width="66" height="6" rx="3" class="dim" />
          <g>
            <rect x="30" y="94" width="88" height="20" rx="6" class="ring-fill" />
            <rect x="38" y="101" width="52" height="5" rx="2.5" class="ink" />
          </g>
          <rect x="36" y="124" width="48" height="6" rx="3" class="dim" />
        </svg>
        <figcaption><span>2</span><p>On a Mac in <strong>Safari</strong>: the <strong>File</strong> menu, then <strong>Add to Dock</strong>.</p></figcaption>
      </figure>

      <figure>
        <svg viewBox="0 0 260 200" role="img" aria-label="Jovii running in its own window">
          <rect x="10" y="16" width="240" height="168" rx="14" class="body" />
          <rect x="18" y="24" width="224" height="152" rx="10" class="screen" />
          <g>
            <rect x="46" y="46" width="168" height="108" rx="12" class="window" />
            <rect x="46" y="46" width="168" height="18" rx="12" class="bar" />
            <rect x="58" y="76" width="40" height="40" rx="10" class="mark" />
            <text x="78" y="103" class="glyph">J</text>
            <rect x="110" y="82" width="86" height="8" rx="4" class="ink" />
            <rect x="110" y="98" width="62" height="8" rx="4" class="dim" />
          </g>
        </svg>
        <figcaption><span>3</span><p>Jovii opens in its own window, in your Dock or taskbar like any other app.</p></figcaption>
      </figure>
    </div>
  {/if}
</div>

<style>
  .install {
    display: grid;
    gap: 26px;
  }
  .tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .tabs button {
    min-height: 42px;
    padding: 0 18px;
    border: 1px solid rgb(255 255 255 / 0.16);
    border-radius: 999px;
    background: rgb(255 255 255 / 0.04);
    color: #c9c3bd;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 180ms ease, color 180ms ease, border-color 180ms ease;
  }
  .tabs button:hover {
    color: #f5f3ed;
  }
  .tabs button.on {
    background: #ef2034;
    border-color: #ef2034;
    color: #fff;
  }
  .tabs button:focus-visible {
    outline: 2px solid #ff9aa6;
    outline-offset: 3px;
  }

  .steps {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 28px;
  }
  figure {
    margin: 0;
    display: grid;
    gap: 14px;
    justify-items: center;
  }
  svg {
    width: 100%;
    max-width: 210px;
    height: auto;
  }
  figcaption {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 10px;
    align-items: start;
  }
  /* The words live in ONE element. When they were loose children of the grid,
     every <strong> became its own cell and the sentence came apart into a
     column of fragments - visible in the first render of this page. */
  figcaption p {
    margin: 0;
    color: #c9c3bd;
    font-size: 0.95rem;
    line-height: 1.55;
  }
  figcaption span {
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: rgb(239 32 52 / 0.16);
    color: #ff9aa6;
    font-size: 0.8rem;
    font-weight: 700;
  }
  figcaption strong {
    color: #f5f3ed;
    font-weight: 650;
  }

  /* The drawn device vocabulary, in one place. */
  .body { fill: #1b1516; stroke: rgb(255 255 255 / 0.14); stroke-width: 1.5; }
  .screen { fill: #0f0c0d; }
  .bar { fill: #241c1e; }
  .sheet { fill: #241c1e; stroke: rgb(255 255 255 / 0.1); }
  .window { fill: #140409; stroke: rgb(255 255 255 / 0.12); }
  .dim { fill: rgb(255 255 255 / 0.12); }
  .ink { fill: rgb(245 243 237 / 0.75); }
  .art { fill: rgb(239 32 52 / 0.18); }
  .mark { fill: #ef2034; }
  .glyph { fill: #fff; font-size: 20px; font-weight: 700; text-anchor: middle; font-family: inherit; }
  .fill { fill: #ff9aa6; }
  .stroke { fill: none; stroke: #ff9aa6; stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }
  .stroke-only { fill: none; stroke: #ff9aa6; stroke-width: 2.2; }
  .ring { fill: rgb(239 32 52 / 0.16); stroke: #ef2034; stroke-width: 2; }
  .ring-fill { fill: rgb(239 32 52 / 0.2); stroke: #ef2034; stroke-width: 1.5; }

  @media (max-width: 900px) {
    .steps {
      grid-template-columns: 1fr;
      gap: 34px;
      max-width: 420px;
    }
  }
</style>
